import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
    DollarSign,
    Clock,
    AlertTriangle,
    TrendingUp,
    FileText,
    Users,
    Calendar,
    ArrowRight,
    Building2,
    ExternalLink,
    X
} from 'lucide-react';
import { formatCurrency, formatDate, daysDiff } from '../lib/utils';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
    LineChart,
    Line
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MESES = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
];

const Dashboard = () => {
    const { isSuperAdmin } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [metricas, setMetricas] = useState(null);
    
    // Período selector - default to current month/year
    const [periodoMes, setPeriodoMes] = useState(new Date().getMonth() + 1);
    const [periodoAnio, setPeriodoAnio] = useState(new Date().getFullYear());
    
    // Modal de servicios pendientes
    const [pendientesModalOpen, setPendientesModalOpen] = useState(false);
    const [serviciosPendientes, setServiciosPendientes] = useState([]);
    const [loadingPendientes, setLoadingPendientes] = useState(false);
    
    // Modal de servicios vencidos
    const [vencidosModalOpen, setVencidosModalOpen] = useState(false);
    const [serviciosVencidos, setServiciosVencidos] = useState([]);
    const [loadingVencidos, setLoadingVencidos] = useState(false);

    // Modal de IVA facturas
    const [ivaModalOpen, setIvaModalOpen] = useState(false);
    const [facturasIva, setFacturasIva] = useState({ items: [], total_iva: 0, total_neto: 0, total_bruto: 0 });
    const [loadingIva, setLoadingIva] = useState(false);

    // Modal de facturación del período
    const [facturacionModalOpen, setFacturacionModalOpen] = useState(false);
    const [serviciosFacturacion, setServiciosFacturacion] = useState([]);
    const [loadingFacturacion, setLoadingFacturacion] = useState(false);

    // Modal de cobranza recuperada
    const [cobranzaModalOpen, setCobranzaModalOpen] = useState(false);
    const [pagosCobranza, setPagosCobranza] = useState({ items: [], total: 0 });
    const [loadingCobranza, setLoadingCobranza] = useState(false);

    useEffect(() => {
        fetchData();
    }, [periodoMes, periodoAnio]);

    const fetchData = async () => {
        try {
            setLoading(true);
            
            if (isSuperAdmin()) {
                const metricasRes = await axios.get(`${API_URL}/api/superadmin/metricas`);
                setMetricas(metricasRes.data);
            }
            
            const dashboardRes = await axios.get(`${API_URL}/api/dashboard?periodo_mes=${periodoMes}&periodo_anio=${periodoAnio}`);
            setDashboardData(dashboardRes.data);
        } catch (error) {
            console.error('Error fetching dashboard:', error);
            if (error.response?.status !== 403) {
                toast.error('Error al cargar el dashboard');
            }
        } finally {
            setLoading(false);
        }
    };
    
    const openPendientesModal = async () => {
        setPendientesModalOpen(true);
        setLoadingPendientes(true);
        try {
            const response = await axios.get(`${API_URL}/api/servicios`, {
                params: {
                    con_saldo_pendiente: true,
                    page_size: 500
                }
            });
            const servicios = response.data.items || response.data;
            const pendientes = servicios.filter(s => parseFloat(s.saldo_pendiente) > 0);
            setServiciosPendientes(pendientes);
        } catch (error) {
            console.error('Error fetching pending services:', error);
            toast.error('Error al cargar servicios pendientes');
        } finally {
            setLoadingPendientes(false);
        }
    };
    
    const openIvaModal = async () => {
        setIvaModalOpen(true);
        setLoadingIva(true);
        try {
            const response = await axios.get(`${API_URL}/api/dashboard/facturas-periodo`, {
                params: { mes: periodoMes, anio: periodoAnio }
            });
            setFacturasIva(response.data);
        } catch (error) {
            console.error('Error fetching facturas IVA:', error);
            toast.error('Error al cargar facturas del período');
        } finally {
            setLoadingIva(false);
        }
    };

    const openFacturacionModal = async () => {
        setFacturacionModalOpen(true);
        setLoadingFacturacion(true);
        try {
            const response = await axios.get(`${API_URL}/api/servicios`, {
                params: {
                    periodo_mes: periodoMes,
                    periodo_anio: periodoAnio,
                    page_size: 500
                }
            });
            const servicios = response.data.items || response.data;
            setServiciosFacturacion(servicios);
        } catch (error) {
            console.error('Error fetching facturacion:', error);
            toast.error('Error al cargar facturación del período');
        } finally {
            setLoadingFacturacion(false);
        }
    };

    const openCobranzaModal = async () => {
        setCobranzaModalOpen(true);
        setLoadingCobranza(true);
        try {
            const response = await axios.get(`${API_URL}/api/dashboard/cobranza-periodo`, {
                params: { mes: periodoMes, anio: periodoAnio }
            });
            setPagosCobranza(response.data);
        } catch (error) {
            console.error('Error fetching cobranza:', error);
            toast.error('Error al cargar cobranza del período');
        } finally {
            setLoadingCobranza(false);
        }
    };

    const openVencidosModal = async () => {
        setVencidosModalOpen(true);
        setLoadingVencidos(true);
        try {
            // Fetch overdue services using the vencidos filter
            const response = await axios.get(`${API_URL}/api/servicios`, {
                params: {
                    vencidos: true,
                    page_size: 100
                }
            });
            const servicios = response.data.items || response.data;
            setServiciosVencidos(servicios);
        } catch (error) {
            console.error('Error fetching overdue services:', error);
            toast.error('Error al cargar servicios vencidos');
        } finally {
            setLoadingVencidos(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9A9A]"></div>
            </div>
        );
    }

    const kpis = dashboardData?.kpis || {};
    const aging = dashboardData?.aging || {};
    const clientes_morosos = dashboardData?.clientes_morosos || [];
    const distribucion = dashboardData?.distribucion_estados || {};
    const evolucion = dashboardData?.evolucion_cobranza || [];

    // Prepare aging data for chart
    const agingData = [
        { name: '0-30 días', value: Number(aging.rango_0_30) || 0, fill: '#10B981' },
        { name: '31-60 días', value: Number(aging.rango_31_60) || 0, fill: '#F59E0B' },
        { name: '61-90 días', value: Number(aging.rango_61_90) || 0, fill: '#F97316' },
        { name: '+90 días', value: Number(aging.rango_90_plus) || 0, fill: '#EF4444' }
    ];

    // Prepare distribution data for pie chart
    const estadoColors = {
        servicio_en_curso: '#64748B',
        pendiente_pago: '#F59E0B',
        pago_parcial: '#F97316',
        pagado: '#10B981',
        vencido: '#EF4444',
        facturado: '#2563EB'
    };

    const pieData = Object.entries(distribucion).map(([key, value]) => ({
        name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: value,
        fill: estadoColors[key] || '#94A3B8'
    }));

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Resumen de cobranza y métricas principales
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Período Selector */}
                    <div className="flex items-center gap-2 bg-white rounded-lg border px-3 py-1.5 shadow-sm">
                        <Calendar size={16} className="text-[#5B9A9A]" />
                        <span className="text-sm font-medium text-slate-600">Período:</span>
                        <Select value={String(periodoMes)} onValueChange={(v) => setPeriodoMes(parseInt(v))}>
                            <SelectTrigger className="w-[120px] h-8 border-0 shadow-none focus:ring-0" data-testid="periodo-mes-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {MESES.map((mes) => (
                                    <SelectItem key={mes.value} value={String(mes.value)}>
                                        {mes.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={String(periodoAnio)} onValueChange={(v) => setPeriodoAnio(parseInt(v))}>
                            <SelectTrigger className="w-[90px] h-8 border-0 shadow-none focus:ring-0" data-testid="periodo-anio-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {[2024, 2025, 2026, 2027, 2028].map((anio) => (
                                    <SelectItem key={anio} value={String(anio)}>
                                        {anio}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button 
                        onClick={fetchData}
                        variant="outline"
                        data-testid="refresh-dashboard"
                    >
                        Actualizar datos
                    </Button>
                </div>
            </div>

            {/* Superadmin metrics */}
            {isSuperAdmin() && metricas && (
                <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white border-0">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Building2 size={20} />
                            <span className="font-semibold">Métricas de Plataforma</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <div>
                                <p className="text-2xl font-bold">{metricas.total_empresas}</p>
                                <p className="text-sm text-slate-300">Empresas totales</p>
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{metricas.empresas_activas}</p>
                                <p className="text-sm text-slate-300">Empresas activas</p>
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{metricas.total_usuarios}</p>
                                <p className="text-sm text-slate-300">Usuarios</p>
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{metricas.total_servicios}</p>
                                <p className="text-sm text-slate-300">Servicios</p>
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{formatCurrency(metricas.monto_total_plataforma)}</p>
                                <p className="text-sm text-slate-300">Monto total</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* KPI Cards - Bento Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                <Card 
                    className="kpi-card cursor-pointer hover:bg-slate-50 transition-colors" 
                    data-testid="kpi-facturacion"
                    onClick={openFacturacionModal}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                                <p className="text-xs text-slate-500 font-medium whitespace-nowrap">
                                    Facturación {MESES.find(m => m.value === periodoMes)?.label?.substring(0, 3)}. {periodoAnio}
                                </p>
                                <p className="text-xl font-bold text-slate-900">
                                    {formatCurrency(kpis.facturacion_mes)}
                                </p>
                            </div>
                            <div className="w-10 h-10 bg-[#D5E8E8] rounded-lg flex items-center justify-center flex-shrink-0">
                                <DollarSign className="text-[#5B9A9A]" size={20} />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card 
                    className="kpi-card border-2 border-orange-200 bg-orange-50 cursor-pointer hover:bg-orange-100 transition-colors" 
                    data-testid="kpi-pendiente-periodo"
                    onClick={openPendientesModal}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                                <p className="text-xs text-orange-700 font-medium whitespace-nowrap">
                                    Pendiente Total
                                </p>
                                <p className="text-xl font-bold text-orange-600">
                                    {formatCurrency(kpis.pendiente_periodo)}
                                </p>
                            </div>
                            <div className="w-10 h-10 bg-orange-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Clock className="text-orange-600" size={20} />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card 
                    className="kpi-card cursor-pointer hover:bg-amber-50 transition-colors" 
                    data-testid="kpi-iva-periodo"
                    onClick={openIvaModal}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                                <p className="text-xs text-slate-500 font-medium whitespace-nowrap">
                                    IVA {MESES.find(m => m.value === periodoMes)?.label?.substring(0, 3)}. {periodoAnio}
                                </p>
                                <p className="text-xl font-bold text-slate-900">
                                    {formatCurrency(kpis.iva_periodo)}
                                </p>
                            </div>
                            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Clock className="text-amber-600" size={20} />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card 
                    className="kpi-card cursor-pointer hover:bg-rose-50 transition-colors" 
                    data-testid="kpi-vencido"
                    onClick={openVencidosModal}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                                <p className="text-xs text-slate-500 font-medium whitespace-nowrap">Monto vencido</p>
                                <p className="text-xl font-bold text-rose-600">
                                    {formatCurrency(kpis.monto_vencido)}
                                </p>
                            </div>
                            <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <AlertTriangle className="text-rose-600" size={20} />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card 
                    className="kpi-card cursor-pointer hover:bg-emerald-50 transition-colors" 
                    data-testid="kpi-recuperada"
                    onClick={openCobranzaModal}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                                <p className="text-xs text-slate-500 font-medium whitespace-nowrap">Cobranza recuperada</p>
                                <p className="text-xl font-bold text-emerald-600">
                                    {formatCurrency(kpis.cobranza_recuperada)}
                                </p>
                            </div>
                            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <TrendingUp className="text-emerald-600" size={20} />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Secondary KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-white">
                    <CardContent className="p-4 flex items-center gap-3">
                        <FileText className="text-slate-400" size={20} />
                        <div>
                            <p className="text-xl font-bold text-slate-900">{kpis.servicios_activos}</p>
                            <p className="text-xs text-slate-500">Servicios activos</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-white">
                    <CardContent className="p-4 flex items-center gap-3">
                        <FileText className="text-amber-500" size={20} />
                        <div>
                            <p className="text-xl font-bold text-slate-900">{kpis.servicios_por_facturar}</p>
                            <p className="text-xs text-slate-500">Por facturar</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-white">
                    <CardContent className="p-4 flex items-center gap-3">
                        <Calendar className="text-[#5B9A9A]" size={20} />
                        <div>
                            <p className="text-xl font-bold text-slate-900">{kpis.proximos_vencimientos}</p>
                            <p className="text-xs text-slate-500">Próx. vencimientos</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-white">
                    <CardContent className="p-4 flex items-center gap-3">
                        <AlertTriangle className="text-rose-500" size={20} />
                        <div>
                            <p className="text-xl font-bold text-rose-600">{formatCurrency(kpis.monto_en_mora)}</p>
                            <p className="text-xs text-slate-500">En mora (+30 días)</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* Aging Chart */}
                <Card className="lg:col-span-5">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Aging de Deuda</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={agingData} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                                    <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                                    <Tooltip 
                                        formatter={(value) => formatCurrency(value)}
                                        contentStyle={{ borderRadius: '8px' }}
                                    />
                                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                        {agingData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Evolution Chart */}
                <Card className="lg:col-span-7">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Evolución de Cobranza</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={evolucion}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                                    <YAxis tickFormatter={(v) => `$${(v/1000000).toFixed(1)}M`} />
                                    <Tooltip 
                                        formatter={(value) => formatCurrency(value)}
                                        contentStyle={{ borderRadius: '8px' }}
                                    />
                                    <Line 
                                        type="monotone" 
                                        dataKey="monto" 
                                        stroke="#2563EB" 
                                        strokeWidth={2}
                                        dot={{ fill: '#2563EB' }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Bottom Row */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* Estado Distribution */}
                <Card className="lg:col-span-4">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Distribución por Estado</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={80}
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend 
                                        layout="vertical" 
                                        align="right"
                                        verticalAlign="middle"
                                        wrapperStyle={{ fontSize: '11px' }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Top Morosos */}
                <Card className="lg:col-span-8">
                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                        <CardTitle className="text-base font-semibold">Clientes con Mayor Morosidad</CardTitle>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/clientes')}
                            className="text-[#5B9A9A]"
                        >
                            Ver todos
                            <ArrowRight size={16} className="ml-1" />
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="table-header">
                                        <th className="text-left py-2 px-3">Cliente</th>
                                        <th className="text-right py-2 px-3">Monto Vencido</th>
                                        <th className="text-right py-2 px-3">Días Mora</th>
                                        <th className="text-center py-2 px-3">Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {clientes_morosos.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="text-center py-8 text-slate-500">
                                                No hay clientes morosos
                                            </td>
                                        </tr>
                                    ) : (
                                        clientes_morosos.slice(0, 5).map((cliente) => (
                                            <tr 
                                                key={cliente.cliente_id} 
                                                className="table-row cursor-pointer"
                                                onClick={() => navigate(`/clientes/${cliente.cliente_id}`)}
                                            >
                                                <td className="table-cell font-medium">{cliente.cliente_nombre}</td>
                                                <td className="table-cell text-right text-rose-600 font-semibold">
                                                    {formatCurrency(cliente.monto_vencido)}
                                                </td>
                                                <td className="table-cell text-right">{cliente.dias_mora} días</td>
                                                <td className="table-cell text-center">
                                                    <Badge 
                                                        className={
                                                            cliente.dias_mora > 90 ? 'badge-error' :
                                                            cliente.dias_mora > 60 ? 'bg-orange-100 text-orange-700' :
                                                            cliente.dias_mora > 30 ? 'badge-warning' :
                                                            'badge-neutral'
                                                        }
                                                    >
                                                        {cliente.dias_mora > 90 ? 'Crítico' :
                                                         cliente.dias_mora > 60 ? 'Alto' :
                                                         cliente.dias_mora > 30 ? 'Medio' : 'Bajo'}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Modal de Cobranza Recuperada */}
            <Dialog open={cobranzaModalOpen} onOpenChange={setCobranzaModalOpen}>
                <DialogContent className="max-w-5xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2" data-testid="cobranza-modal-title">
                            <TrendingUp className="text-emerald-600" size={20} />
                            Cobranza Recuperada - {MESES.find(m => m.value === periodoMes)?.label} {periodoAnio}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto flex-1">
                        {loadingCobranza ? (
                            <div className="flex justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
                            </div>
                        ) : pagosCobranza.items.length === 0 ? (
                            <p className="text-center text-slate-500 py-8">No hay pagos registrados en este período</p>
                        ) : (
                            <div className="space-y-2">
                                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-100 rounded-lg text-xs font-medium text-slate-600">
                                    <div className="col-span-3">Cliente / Servicio</div>
                                    <div className="col-span-2 text-center">Fecha Pago</div>
                                    <div className="col-span-2 text-right">Monto</div>
                                    <div className="col-span-1 text-center">Días Mora</div>
                                    <div className="col-span-2 text-center">Método</div>
                                    <div className="col-span-2 text-center">Tipo</div>
                                </div>
                                {pagosCobranza.items.map((p) => (
                                    <div key={p.id} className="grid grid-cols-12 gap-2 px-3 py-3 bg-white border rounded-lg items-center hover:bg-slate-50">
                                        <div className="col-span-3">
                                            <p className="font-medium text-sm text-slate-900 truncate">{p.cliente_nombre || 'Sin cliente'}</p>
                                            <p className="text-xs text-slate-500 truncate">{p.nombre_producto}</p>
                                        </div>
                                        <div className="col-span-2 text-center">
                                            <span className="text-sm text-slate-600">{formatDate(p.fecha_pago)}</span>
                                        </div>
                                        <div className="col-span-2 text-right">
                                            <span className="text-sm font-bold text-emerald-600">{formatCurrency(p.monto)}</span>
                                        </div>
                                        <div className="col-span-1 text-center">
                                            <Badge variant="outline" className={`text-[10px] ${
                                                p.dias_mora > 90 ? 'bg-red-100 text-red-800 border-red-300' :
                                                p.dias_mora > 60 ? 'bg-orange-100 text-orange-700 border-orange-300' :
                                                'bg-amber-100 text-amber-700 border-amber-300'
                                            }`}>{p.dias_mora}d</Badge>
                                        </div>
                                        <div className="col-span-2 text-center">
                                            <Badge variant="outline" className="text-xs">
                                                {p.metodo_pago?.replace(/_/g, ' ') || '-'}
                                            </Badge>
                                        </div>
                                        <div className="col-span-2 text-center">
                                            <span className="text-xs text-slate-500">{p.es_pago_parcial ? 'Parcial' : 'Total'}</span>
                                        </div>
                                    </div>
                                ))}
                                <div className="grid grid-cols-12 gap-2 px-3 py-3 bg-emerald-50 border-2 border-emerald-300 rounded-lg items-center mt-4">
                                    <div className="col-span-3">
                                        <p className="font-bold text-sm text-slate-900">Total ({pagosCobranza.items.length} pagos)</p>
                                    </div>
                                    <div className="col-span-2"></div>
                                    <div className="col-span-2 text-right">
                                        <p className="text-sm font-bold text-emerald-600">{formatCurrency(pagosCobranza.total)}</p>
                                    </div>
                                    <div className="col-span-5"></div>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modal de Facturación del Período */}
            <Dialog open={facturacionModalOpen} onOpenChange={setFacturacionModalOpen}>
                <DialogContent className="max-w-5xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2" data-testid="facturacion-modal-title">
                            <DollarSign className="text-[#5B9A9A]" size={20} />
                            Facturación - {MESES.find(m => m.value === periodoMes)?.label} {periodoAnio}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto flex-1">
                        {loadingFacturacion ? (
                            <div className="flex justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5B9A9A]" />
                            </div>
                        ) : serviciosFacturacion.length === 0 ? (
                            <p className="text-center text-slate-500 py-8">No hay servicios en este período</p>
                        ) : (
                            <div className="space-y-2">
                                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-100 rounded-lg text-xs font-medium text-slate-600">
                                    <div className="col-span-3">Cliente / Servicio</div>
                                    <div className="col-span-2 text-right">Neto</div>
                                    <div className="col-span-2 text-right">IVA</div>
                                    <div className="col-span-2 text-right">Total</div>
                                    <div className="col-span-2 text-center">Estado</div>
                                    <div className="col-span-1 text-center">Acción</div>
                                </div>
                                {serviciosFacturacion.map((s) => (
                                    <div key={s.id} className="grid grid-cols-12 gap-2 px-3 py-3 bg-white border rounded-lg items-center hover:bg-slate-50">
                                        <div className="col-span-3">
                                            <p className="font-medium text-sm text-slate-900 truncate">{s.cliente_nombre || 'Sin cliente'}</p>
                                            <p className="text-xs text-slate-500 truncate">{s.nombre_producto}</p>
                                        </div>
                                        <div className="col-span-2 text-right">
                                            <span className="text-sm text-slate-700">{formatCurrency(s.monto_neto)}</span>
                                        </div>
                                        <div className="col-span-2 text-right">
                                            <span className="text-sm text-amber-600">{formatCurrency(s.iva)}</span>
                                        </div>
                                        <div className="col-span-2 text-right">
                                            <span className="text-sm font-medium text-slate-900">{formatCurrency(s.monto_total)}</span>
                                        </div>
                                        <div className="col-span-2 text-center">
                                            <Badge variant="outline" className="text-xs whitespace-nowrap">
                                                {s.estado?.replace(/_/g, ' ')}
                                            </Badge>
                                        </div>
                                        <div className="col-span-1 text-center">
                                            <Button 
                                                variant="ghost" size="sm"
                                                className="text-[#5B9A9A] hover:text-[#2D5A5A] hover:bg-[#E8F4F4]"
                                                onClick={() => { setFacturacionModalOpen(false); navigate(`/servicios/${s.id}`); }}
                                            >
                                                <ExternalLink size={14} className="mr-1" /> Ver
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                                <div className="grid grid-cols-12 gap-2 px-3 py-3 bg-[#E8F4F4] border-2 border-[#5B9A9A] rounded-lg items-center mt-4">
                                    <div className="col-span-3">
                                        <p className="font-bold text-sm text-slate-900">Total ({serviciosFacturacion.length} servicios)</p>
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <p className="text-sm font-bold text-slate-700">{formatCurrency(serviciosFacturacion.reduce((a, s) => a + parseFloat(s.monto_neto || 0), 0))}</p>
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <p className="text-sm font-bold text-amber-600">{formatCurrency(serviciosFacturacion.reduce((a, s) => a + parseFloat(s.iva || 0), 0))}</p>
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <p className="text-sm font-bold text-slate-900">{formatCurrency(serviciosFacturacion.reduce((a, s) => a + parseFloat(s.monto_total || 0), 0))}</p>
                                    </div>
                                    <div className="col-span-3"></div>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modal de Servicios Pendientes del Período */}
            <Dialog open={pendientesModalOpen} onOpenChange={setPendientesModalOpen}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Clock className="text-orange-600" size={20} />
                            Servicios Pendientes de Pago
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-auto">
                        {loadingPendientes ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                            </div>
                        ) : serviciosPendientes.length === 0 ? (
                            <div className="text-center py-8 text-slate-500">
                                No hay servicios pendientes de pago
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {/* Header */}
                                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-100 rounded-lg text-xs font-medium text-slate-600">
                                    <div className="col-span-4">Cliente / Servicio</div>
                                    <div className="col-span-2 text-right">Monto Total</div>
                                    <div className="col-span-2 text-right">Pendiente</div>
                                    <div className="col-span-2 text-center">Estado</div>
                                    <div className="col-span-2 text-center">Acción</div>
                                </div>
                                
                                {/* List */}
                                {serviciosPendientes.map((servicio) => (
                                    <div 
                                        key={servicio.id} 
                                        className="grid grid-cols-12 gap-2 px-3 py-3 bg-white border rounded-lg items-center hover:bg-slate-50"
                                    >
                                        <div className="col-span-4">
                                            <p className="font-medium text-sm text-slate-900 truncate">
                                                {servicio.cliente_nombre || 'Sin cliente'}
                                            </p>
                                            <p className="text-xs text-slate-500 truncate">
                                                {servicio.nombre_producto || servicio.descripcion}
                                            </p>
                                        </div>
                                        <div className="col-span-2 text-right">
                                            <p className="text-sm font-medium">{formatCurrency(servicio.monto_total)}</p>
                                        </div>
                                        <div className="col-span-2 text-right">
                                            <p className="text-sm font-bold text-orange-600">{formatCurrency(servicio.saldo_pendiente)}</p>
                                        </div>
                                        <div className="col-span-2 text-center">
                                            <Badge 
                                                variant="outline" 
                                                className={`text-xs ${
                                                    servicio.estado === 'pagado' ? 'bg-green-50 text-green-700 border-green-200' :
                                                    servicio.estado === 'pago_parcial' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                                    servicio.estado === 'vencido' || servicio.estado === 'en_mora' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                                                    'bg-slate-50 text-slate-700 border-slate-200'
                                                }`}
                                            >
                                                {servicio.estado?.replace(/_/g, ' ')}
                                            </Badge>
                                        </div>
                                        <div className="col-span-2 text-center">
                                            <Button 
                                                variant="ghost" 
                                                size="sm"
                                                className="text-[#5B9A9A] hover:text-[#2D5A5A] hover:bg-[#E8F4F4]"
                                                onClick={() => {
                                                    setPendientesModalOpen(false);
                                                    navigate(`/servicios/${servicio.id}`);
                                                }}
                                            >
                                                <ExternalLink size={14} className="mr-1" />
                                                Ver
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                                
                                {/* Summary */}
                                <div className="grid grid-cols-12 gap-2 px-3 py-3 bg-orange-50 border-2 border-orange-200 rounded-lg items-center mt-4">
                                    <div className="col-span-4">
                                        <p className="font-bold text-sm text-slate-900">
                                            Total ({serviciosPendientes.length} servicios)
                                        </p>
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <p className="text-sm font-bold">
                                            {formatCurrency(serviciosPendientes.reduce((acc, s) => acc + parseFloat(s.monto_total || 0), 0))}
                                        </p>
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <p className="text-sm font-bold text-orange-600">
                                            {formatCurrency(serviciosPendientes.reduce((acc, s) => acc + parseFloat(s.saldo_pendiente || 0), 0))}
                                        </p>
                                    </div>
                                    <div className="col-span-4"></div>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modal de IVA Facturas del Período */}
            <Dialog open={ivaModalOpen} onOpenChange={setIvaModalOpen}>
                <DialogContent className="max-w-5xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2" data-testid="iva-modal-title">
                            <Clock className="text-amber-600" size={20} />
                            Facturas Emitidas - {MESES.find(m => m.value === periodoMes)?.label} {periodoAnio}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto flex-1">
                        {loadingIva ? (
                            <div className="flex justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5B9A9A]" />
                            </div>
                        ) : facturasIva.items.length === 0 ? (
                            <p className="text-center text-slate-500 py-8">No hay facturas emitidas en este período</p>
                        ) : (
                            <div className="space-y-2">
                                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-100 rounded-lg text-xs font-medium text-slate-600">
                                    <div className="col-span-3">Cliente / Servicio</div>
                                    <div className="col-span-1 text-center">N° Fact.</div>
                                    <div className="col-span-2 text-center">Fecha Fact.</div>
                                    <div className="col-span-2 text-right">Neto</div>
                                    <div className="col-span-2 text-right">IVA</div>
                                    <div className="col-span-2 text-right">Total</div>
                                </div>
                                {facturasIva.items.map((f) => (
                                    <div key={f.id} className="grid grid-cols-12 gap-2 px-3 py-3 bg-white border rounded-lg items-center hover:bg-slate-50">
                                        <div className="col-span-3">
                                            <p className="font-medium text-sm text-slate-900 truncate">{f.cliente_nombre || 'Sin cliente'}</p>
                                            <p className="text-xs text-slate-500 truncate">{f.nombre_producto}</p>
                                        </div>
                                        <div className="col-span-1 text-center">
                                            <span className="text-xs text-slate-600">{f.numero_factura || '-'}</span>
                                        </div>
                                        <div className="col-span-2 text-center">
                                            <span className="text-sm text-slate-600">{formatDate(f.fecha_facturacion)}</span>
                                        </div>
                                        <div className="col-span-2 text-right">
                                            <span className="text-sm text-slate-700">{formatCurrency(f.monto_neto)}</span>
                                        </div>
                                        <div className="col-span-2 text-right">
                                            <span className="text-sm font-semibold text-amber-600">{formatCurrency(f.iva)}</span>
                                        </div>
                                        <div className="col-span-2 text-right">
                                            <span className="text-sm font-medium text-slate-900">{formatCurrency(f.monto_total)}</span>
                                        </div>
                                    </div>
                                ))}
                                <div className="grid grid-cols-12 gap-2 px-3 py-3 bg-amber-50 border-2 border-amber-200 rounded-lg items-center mt-4">
                                    <div className="col-span-3">
                                        <p className="font-bold text-sm text-slate-900">Total ({facturasIva.items.length} facturas)</p>
                                    </div>
                                    <div className="col-span-1"></div>
                                    <div className="col-span-2"></div>
                                    <div className="col-span-2 text-right">
                                        <p className="text-sm font-bold text-slate-700">{formatCurrency(facturasIva.total_neto)}</p>
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <p className="text-sm font-bold text-amber-600">{formatCurrency(facturasIva.total_iva)}</p>
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <p className="text-sm font-bold text-slate-900">{formatCurrency(facturasIva.total_bruto)}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* Modal de Servicios Vencidos */}
            <Dialog open={vencidosModalOpen} onOpenChange={setVencidosModalOpen}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="text-rose-600" size={20} />
                            Servicios Vencidos
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-auto">
                        {loadingVencidos ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
                            </div>
                        ) : serviciosVencidos.length === 0 ? (
                            <div className="text-center py-8 text-slate-500">
                                No hay servicios vencidos
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {/* Header */}
                                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-100 rounded-lg text-xs font-medium text-slate-600">
                                    <div className="col-span-3">Cliente / Servicio</div>
                                    <div className="col-span-1 text-center">Período</div>
                                    <div className="col-span-2 text-right">Pendiente</div>
                                    <div className="col-span-2 text-center">Vencimiento</div>
                                    <div className="col-span-1 text-center">Días</div>
                                    <div className="col-span-2 text-center">Estado</div>
                                    <div className="col-span-1 text-center">Acción</div>
                                </div>
                                
                                {/* List */}
                                {serviciosVencidos.map((servicio) => {
                                    const fechaVenc = servicio.fecha_vencimiento ? new Date(servicio.fecha_vencimiento) : null;
                                    const diasVencido = fechaVenc ? Math.floor((new Date() - fechaVenc) / (1000 * 60 * 60 * 24)) : 0;
                                    
                                    return (
                                        <div 
                                            key={servicio.id} 
                                            className="grid grid-cols-12 gap-2 px-3 py-3 bg-white border rounded-lg items-center hover:bg-slate-50"
                                        >
                                            <div className="col-span-3">
                                                <p className="font-medium text-sm text-slate-900 truncate">
                                                    {servicio.cliente_nombre || 'Sin cliente'}
                                                </p>
                                                <p className="text-xs text-slate-500 truncate">
                                                    {servicio.nombre_producto || servicio.descripcion}
                                                </p>
                                            </div>
                                            <div className="col-span-1 text-center">
                                                <p className="text-xs text-slate-600">{servicio.periodo_mes}/{servicio.periodo_anio}</p>
                                            </div>
                                            <div className="col-span-2 text-right">
                                                <p className="text-sm font-bold text-rose-600">{formatCurrency(servicio.saldo_pendiente)}</p>
                                            </div>
                                            <div className="col-span-2 text-center">
                                                <p className="text-sm text-slate-600">{formatDate(servicio.fecha_vencimiento)}</p>
                                            </div>
                                            <div className="col-span-1 text-center">
                                                <Badge 
                                                    variant="outline" 
                                                    className={`text-[10px] ${
                                                        diasVencido > 90 ? 'bg-red-100 text-red-800 border-red-300' :
                                                        diasVencido > 60 ? 'bg-rose-100 text-rose-700 border-rose-300' :
                                                        diasVencido > 30 ? 'bg-orange-100 text-orange-700 border-orange-300' :
                                                        'bg-amber-100 text-amber-700 border-amber-300'
                                                    }`}
                                                >
                                                    {diasVencido}d
                                                </Badge>
                                            </div>
                                            <div className="col-span-2 text-center">
                                                <Badge 
                                                    variant="outline" 
                                                    className="text-xs bg-rose-50 text-rose-700 border-rose-200 whitespace-nowrap"
                                                >
                                                    {servicio.estado?.replace(/_/g, ' ')}
                                                </Badge>
                                            </div>
                                            <div className="col-span-1 text-center">
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm"
                                                    className="text-[#5B9A9A] hover:text-[#2D5A5A] hover:bg-[#E8F4F4]"
                                                    onClick={() => {
                                                        setVencidosModalOpen(false);
                                                        navigate(`/servicios/${servicio.id}`);
                                                    }}
                                                >
                                                    <ExternalLink size={14} className="mr-1" />
                                                    Ver
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                                
                                {/* Summary */}
                                <div className="grid grid-cols-12 gap-2 px-3 py-3 bg-rose-50 border-2 border-rose-200 rounded-lg items-center mt-4">
                                    <div className="col-span-4">
                                        <p className="font-bold text-sm text-slate-900">
                                            Total ({serviciosVencidos.length} servicios)
                                        </p>
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <p className="text-sm font-bold text-rose-600">
                                            {formatCurrency(serviciosVencidos.reduce((acc, s) => acc + parseFloat(s.saldo_pendiente || 0), 0))}
                                        </p>
                                    </div>
                                    <div className="col-span-6"></div>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Dashboard;
